/**
 * Which build is running, and which build is deployed — as data.
 *
 * Both sides of that comparison are untrusted here: one is JSON fetched from the
 * origin, the other is a build-time substitution that is only ever as correct as
 * the webpack config that produced it. Passing both through the same parser is
 * what makes "these differ" mean "the deploy moved" rather than "one of them
 * arrived in a shape nobody expected".
 */

export const VERSION_MANIFEST_URL = '/version.json';

function readTrimmedString(value) {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function normalizeRuntimeVersion(version) {
    if (!version || typeof version !== 'object' || Array.isArray(version)) {
        return null;
    }

    const appVersion = readTrimmedString(version.appVersion);
    const buildId = readTrimmedString(version.buildId);

    // A manifest missing either field cannot answer the only question asked of
    // it, so it is not a manifest. Returning a half-filled object instead would
    // compare `undefined` against `undefined` and report the app up to date for
    // as long as the mistake lived.
    if (!appVersion || !buildId) {
        return null;
    }

    return {
        appVersion,
        buildId,
        releaseChannel: readTrimmedString(version.releaseChannel) || 'development',
        manifestUrl: readTrimmedString(version.manifestUrl) || VERSION_MANIFEST_URL,
    };
}

/**
 * Inequality, deliberately — not "is greater than".
 *
 * A rollback deploys an *older* `appVersion` and has to be adopted just as
 * eagerly as a release; ordering the two would pin every client to the broken
 * build the rollback exists to undo. `buildId` carries the deploy timestamp, so
 * it also separates two deploys that never bumped the version number.
 */
export function hasDeployedRuntimeVersionChanged(currentVersion, deployedVersion) {
    const current = normalizeRuntimeVersion(currentVersion);
    const deployed = normalizeRuntimeVersion(deployedVersion);

    if (!current || !deployed) {
        return false;
    }

    return current.buildId !== deployed.buildId;
}
